import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Wrench, Cpu, Cog, Award, MapPin, Phone,
  GraduationCap, Building2, Wallet, CheckCircle2, Flag,
} from "lucide-react";
import { EditableText } from "./live/Editable";

/* ---------- Data extracted from the official Kiki flyer ---------- */
const TRACKS = [
  {
    key: "tool-die",
    icon: Wrench,
    title: "Tool & Die Making",
    subtitle: "Mechanical",
    accent: "from-yellow-400 to-yellow-500",
    modules: [
      "Benchwork, Metrology",
      "Engineering Drawing, Quality",
      "Material Science",
      "Milling, Turning, Grinding, CNC",
      "EDM, Wire Cut, Jig & Fixture",
      "Presstool & Mould Design",
      "Pneumatics & Hydraulics",
      "AutoCAD, CAM",
      "English, Mathematics",
      "Computers, Interview & Soft Skills",
    ],
    slug: "tool-die-design",
  },
  {
    key: "mechatronics",
    icon: Cpu,
    title: "Mechatronics",
    subtitle: "Electrical & Electronics",
    accent: "from-blue-500 to-blue-700",
    modules: [
      "Benchwork, Metrology",
      "Engineering Drawing",
      "Material Science",
      "Milling, Turning, Grinding, CNC",
      "Electrical & Electronics",
      "Pneumatics & Hydraulics",
      "Drive & Circuit, PLC, BUS",
      "Microprocessor, Control Room",
      "English, Mathematics",
      "Computers, Interview & Soft Skills",
    ],
    slug: "mechatronics-technician",
  },
  {
    key: "precision",
    icon: Cog,
    title: "Precision Machinist",
    subtitle: "Mechanical",
    accent: "from-yellow-400 to-yellow-500",
    modules: [
      "Benchwork, Metrology",
      "Engineering Drawing, Quality",
      "Material Science",
      "Milling, Turning, Grinding",
      "CNC, VMC, EDM, Wire Cut",
      "AutoCAD",
      "English, Mathematics",
      "Computers, Interview & Soft Skills",
    ],
    slug: "precision-machining",
  },
];

const FEATURES = [
  { icon: Wallet, label: "Earn While You Learn", key: "earn" },
  { icon: Building2, label: "Accommodation Available", key: "accommodation" },
  { icon: Award, label: "Dual Certificate — HWK Koblenz + Govt of India", key: "dual" },
  { icon: GraduationCap, label: "Strong Placement Record", key: "placement" },
  { icon: Flag, label: "Jobs in India & Abroad", key: "jobs" },
];

const CAREERS = [
  "Tool Room", "Tool Design", "Production Engineer", "Vendor Development",
  "Machine Shop Engineer", "Assembly Shop Engineer", "Maintenance",
  "Development / Automation", "Quality Control", "Production (Electronics)",
  "Service Engineering", "PLC Programmer", "CNC Engineer", "CNC Programmer",
  "Instrumentation",
];

const COMPANIES = [
  { name: "Maruti Suzuki", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "Mikuni", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "Sanko", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "Nagata India", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "Koide", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "Hi-lex", tag: "Japanese-MNC", color: "bg-red-500" },
  { name: "IFB", tag: "Indian-MNC", color: "bg-orange-500" },
  { name: "Motherson", tag: "Indian-MNC", color: "bg-orange-500" },
  { name: "Spinks", tag: "Indian-MNC", color: "bg-orange-500" },
  { name: "Sandhar", tag: "Indian-MNC", color: "bg-orange-500" },
  { name: "Subros", tag: "Indian-MNC", color: "bg-orange-500" },
  { name: "Rico", tag: "Indian Company", color: "bg-orange-500" },
  { name: "ATMA", tag: "Indian Company", color: "bg-orange-500" },
  { name: "Roop Polymers", tag: "Indian Company", color: "bg-orange-500" },
  { name: "Stryker", tag: "American-MNC", color: "bg-blue-600" },
  { name: "Boston Scientific", tag: "American-MNC", color: "bg-blue-600" },
  { name: "Bucher Hydraulics", tag: "German-MNC", color: "bg-black" },
];

export default function AdmissionShowcase() {
  return (
    <section
      id="admission-showcase"
      className="relative py-24 bg-gradient-to-b from-white via-yellow-50/40 to-white overflow-hidden"
    >
      {/* Decorative shapes */}
      <div className="absolute -top-24 -left-24 w-80 h-80 bg-yellow-300/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header banner — replaces the flyer's big yellow header */}
        <div className="relative overflow-hidden rounded-3xl bg-[#0B2A6B] text-white shadow-2xl mb-16">
          <div className="absolute inset-0 opacity-[0.08]"
               style={{ backgroundImage: "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)", backgroundSize: "40px 40px" }} />
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-yellow-400/30 blur-3xl rounded-full" />
          <div className="relative grid md:grid-cols-5 gap-6 p-8 md:p-12 items-center">
            <div className="md:col-span-3">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold tracking-wider uppercase">
                <Award className="h-3.5 w-3.5" /> Admission Open • 2025
              </span>
              <EditableText
                path="home.admission.title"
                as="h2"
                className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight"
              >
                Earn While You Learn
              </EditableText>
              <EditableText
                path="home.admission.subtitle"
                as="p"
                multiline
                className="mt-3 text-lg text-slate-200 max-w-xl"
              >
                3½ Years German Technical Training Course for 10th / 12th pass students (all subjects). HWK Koblenz + Government of India dual certification.
              </EditableText>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/admissions"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-primary animate-pulse-glow"
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:+918800288994"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-outline-light"
                >
                  <Phone className="h-4 w-4" /> Call 8800288994
                </a>
              </div>
            </div>

            <div className="md:col-span-2 grid grid-cols-2 gap-3">
              <StatBox big="3½" small="Year Program" />
              <StatBox big="₹4.8L" small="Estimated Starting" />
              <StatBox big="10+12" small="Both Streams" />
              <StatBox big="Dual" small="Certification" />
            </div>
          </div>
        </div>

        {/* Features chips */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-16">
          {FEATURES.map(({ icon: Icon, label, key }) => (
            <div
              key={key}
              className="flex items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-sm border border-slate-100 hover:border-yellow-300 hover:shadow-md transition"
            >
              <div className="h-10 w-10 rounded-lg bg-yellow-400/15 text-yellow-600 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5" />
              </div>
              <EditableText
                path={`home.admission.feature.${key}`}
                as="span"
                className="text-sm font-semibold text-slate-700"
              >
                {label}
              </EditableText>
            </div>
          ))}
        </div>

        {/* Course tracks */}
        <div className="text-center mb-10">
          <p className="kicker mb-3">Course / Trade</p>
          <h3 className="text-3xl sm:text-4xl font-bold text-[#0B2A6B]">
            Three specialisation tracks, one German curriculum
          </h3>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            All tracks run for <strong>3.5 years</strong> and are dual-certified. Choose your stream at the start of Year 2.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-16">
          {TRACKS.map((t) => (
            <div
              key={t.key}
              className="group relative rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition overflow-hidden"
              data-testid={`track-${t.key}`}
            >
              <div className={`h-2 bg-gradient-to-r ${t.accent}`} />
              <div className="p-7">
                <div className="flex items-start justify-between mb-4">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${t.accent} flex items-center justify-center shadow-md`}>
                    <t.icon className="h-7 w-7 text-[#0B2A6B]" strokeWidth={2.2} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    3.5 years
                  </span>
                </div>
                <p className="text-[11px] uppercase tracking-widest text-yellow-600 font-bold">
                  {t.subtitle}
                </p>
                <h4 className="text-xl font-bold text-[#0B2A6B] mt-1 mb-4">{t.title}</h4>
                <ul className="space-y-2">
                  {t.modules.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                      {m}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/courses/detail/${t.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0B2A6B] group-hover:text-yellow-600 transition"
                >
                  Course details <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Salary + Certification callout */}
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          <div className="relative rounded-3xl bg-gradient-to-br from-yellow-400 to-yellow-500 p-8 text-[#0B2A6B] shadow-lg overflow-hidden">
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-white/25 rounded-full blur-2xl" />
            <span className="text-xs font-black uppercase tracking-widest">Estimated Salary</span>
            <div className="text-4xl sm:text-5xl font-black mt-2">₹1.5 – 4.8 LPA</div>
            <p className="mt-2 text-sm font-semibold opacity-90">
              Starting salary range for our graduates across leading Indian & international manufacturers.
            </p>
          </div>
          <div className="relative rounded-3xl bg-[#0B2A6B] text-white p-8 shadow-lg overflow-hidden">
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-yellow-400/20 rounded-full blur-2xl" />
            <span className="text-xs font-black uppercase tracking-widest text-yellow-400">Dual Certification</span>
            <div className="mt-3 space-y-2 text-sm">
              <p className="flex items-start gap-2"><Award className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" /> German Chamber of Skilled Crafts (HWK), Koblenz — Germany</p>
              <p className="flex items-start gap-2"><Award className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" /> Government of India — MSDE, NSDC</p>
            </div>
            <Link
              to="/about/certifications"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-yellow-400 hover:text-yellow-300"
            >
              View certifications <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Career opportunities */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <p className="kicker mb-2">Career Opportunities</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#0B2A6B]">
              Roles our graduates step into
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {CAREERS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-yellow-400 hover:text-[#0B2A6B] hover:border-yellow-400 transition"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* Companies grid */}
        <div>
          <div className="text-center mb-8">
            <p className="kicker mb-2">Placement</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#0B2A6B]">
              Companies where our students train & work
            </h3>
            <p className="text-slate-600 mt-2 text-sm">Jobs in India & Abroad — across Japanese, American, German and Indian manufacturers.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {COMPANIES.map((c) => (
              <div
                key={c.name}
                className="group relative bg-white rounded-2xl border border-slate-100 px-4 py-5 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition"
              >
                <span
                  className={`absolute top-2 right-2 h-2 w-2 rounded-full ${c.color}`}
                  title={c.tag}
                />
                <div className="font-bold text-[#0B2A6B] text-sm sm:text-base leading-tight">
                  {c.name}
                </div>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-yellow-600">
                  {c.tag}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Registration centres strip */}
        <div className="mt-16 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-yellow-500" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">Main Campus</span>
            </div>
            <p className="font-bold text-[#0B2A6B]">Kiki Centre for Technology</p>
            <p className="text-sm text-slate-600 mt-1">1/47, Sector 75A, Gurugram, Haryana 122012</p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <a href="tel:+918800288994" className="text-[#0B2A6B] font-semibold hover:text-yellow-600">
                +91 88002 88994
              </a>
              <span className="text-slate-300">|</span>
              <a href="tel:+919717283555" className="text-[#0B2A6B] font-semibold hover:text-yellow-600">
                +91 97172 83555
              </a>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-yellow-500" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">Registration Centre</span>
            </div>
            <p className="font-bold text-[#0B2A6B]">T-23, Anjali Foundation</p>
            <p className="text-sm text-slate-600 mt-1">Near Blinkit Store, Sector 1, IMT Manesar, Gurgaon, Haryana 122052</p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <a href="tel:+911244060694" className="text-[#0B2A6B] font-semibold hover:text-yellow-600">
                0124-4060694 / 95
              </a>
              <span className="text-slate-300">|</span>
              <a href="mailto:info@kikicentre.com" className="text-[#0B2A6B] font-semibold hover:text-yellow-600">
                info@kikicentre.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatBox({ big, small }) {
  return (
    <div className="rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm px-4 py-4 text-center">
      <div className="text-2xl sm:text-3xl font-black text-yellow-400 leading-none">{big}</div>
      <div className="text-[10px] tracking-widest uppercase text-slate-300 mt-1.5">{small}</div>
    </div>
  );
}
